import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { colors } from '../theme/tokens';
import { useToast } from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PERIODS = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'all', label: 'All Time' }
];

// Conversion columns. Only lead→stable earns points (10); the down-conversions
// are tracked for visibility but don't score.
const COLS = [
  { key: 'stable', label: 'Lead → Stable', color: '#2fb65c', points: 5 },
  { key: 'unstable', label: 'Stable → Unstable', color: '#f4d03f', points: 0 },
  { key: 'technical', label: 'Stable → Technical', color: '#9b59b6', points: 0 }
];

// Per-user conversion tracking, driven by the /conversions endpoint (which
// reads the shared task-analytics activity log). Lives under the leads
// Analytics tab as the "Conversion" view.
const ConversionAnalytics = ({ token }) => {
  const showToast = useToast();
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/api/client-markers/conversions`, {
        params: { period },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) showToast('Failed to load conversions', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, token, showToast]);

  const users = data?.users || [];

  const summary = useMemo(() => {
    const t = data?.totals || {};
    return {
      stable: t.lead_convert_stable || 0,
      unstable: t.client_convert_unstable || 0,
      technical: t.client_convert_technical || 0,
      points: t.points || 0
    };
  }, [data]);

  return (
    <div style={styles.container}>
      <div style={styles.head}>
        <h3 style={styles.title}>🔁 Conversions</h3>
        <div style={styles.periods}>
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              style={{ ...styles.periodBtn, ...(period === p.value ? styles.periodBtnActive : {}) }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryNum, color: colors.parGreenBright }}>{summary.points}</div>
          <div style={styles.summaryLabel}>Points earned</div>
        </div>
        {COLS.map((c) => (
          <div key={c.key} style={styles.summaryCard}>
            <div style={{ ...styles.summaryNum, color: c.color }}>{summary[c.key]}</div>
            <div style={styles.summaryLabel}>{c.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={styles.state}>Loading…</div>
      ) : users.length === 0 ? (
        <div style={styles.state}>
          <div style={{ fontSize: '38px', marginBottom: '8px' }}>🔁</div>
          <p>No conversions logged in this period yet.</p>
          <p style={styles.stateHint}>Conversions are recorded when a client's status changes (e.g. a New Lead becomes a Stable Client).</p>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, textAlign: 'left' }}>Team Member</th>
                {COLS.map((c) => (
                  <th key={c.key} style={styles.th}>
                    <span style={{ ...styles.colDot, background: c.color }} /> {c.label}
                  </th>
                ))}
                <th style={{ ...styles.th, color: colors.parGreenBright }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.userId} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.memberCell}>
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} style={styles.avatar} />
                      ) : (
                        <div style={styles.avatarPlaceholder}>{(u.name || '?')[0].toUpperCase()}</div>
                      )}
                      <div>
                        <div style={styles.memberName}>{u.name}</div>
                        {u.salesRole && (
                          <div style={styles.roleBadge}>{u.salesRole === 'manager' ? 'Manager' : 'Rep'}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  {COLS.map((c) => (
                    <td key={c.key} style={{ ...styles.td, ...styles.tdNum, color: u[c.key] ? colors.text : colors.textFaint }}>
                      {u[c.key] || '·'}
                    </td>
                  ))}
                  <td style={{ ...styles.td, ...styles.tdNum, color: colors.parGreenBright, fontWeight: 800 }}>{u.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '20px' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' },
  title: { margin: 0, fontSize: '16px', fontWeight: '700', color: colors.text },
  periods: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  periodBtn: {
    padding: '6px 12px', borderRadius: '20px', border: `1px solid ${colors.border}`,
    background: colors.surface, color: colors.textMuted, fontSize: '12px', fontWeight: '600', cursor: 'pointer'
  },
  periodBtnActive: { background: 'rgba(47,182,92,0.16)', borderColor: 'rgba(47,182,92,0.55)', color: colors.text },
  summaryRow: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  summaryCard: {
    flex: '1 1 120px', background: colors.glass, padding: '16px 18px', borderRadius: '10px',
    textAlign: 'center', border: `1px solid ${colors.glassBorder}`, boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
  },
  summaryNum: { fontSize: '30px', fontWeight: '800', lineHeight: 1 },
  summaryLabel: { fontSize: '12px', color: colors.textMuted, marginTop: '6px', fontWeight: '500' },
  state: { textAlign: 'center', padding: '50px 20px', color: colors.textMuted },
  stateHint: { fontSize: '12px', color: colors.textFaint, marginTop: '6px', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' },
  tableWrapper: { overflowX: 'auto', borderRadius: '8px', border: `1px solid ${colors.glassBorder}` },
  table: { width: '100%', borderCollapse: 'collapse', background: colors.glass, minWidth: '520px' },
  th: {
    textAlign: 'center', padding: '12px 14px', background: colors.surfaceSoft, fontWeight: '600',
    fontSize: '12.5px', color: colors.text, borderBottom: `2px solid ${colors.border}`, whiteSpace: 'nowrap'
  },
  colDot: { display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', marginRight: '4px' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}` },
  td: { padding: '12px 14px', fontSize: '13px', color: colors.text, verticalAlign: 'middle' },
  tdNum: { textAlign: 'center', fontWeight: '700', fontVariantNumeric: 'tabular-nums' },
  memberCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', border: `2px solid ${colors.border}`, flexShrink: 0 },
  avatarPlaceholder: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #2fb65c, #1d8c3a)', color: 'white',
    fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  memberName: { fontWeight: '600', fontSize: '13px', color: colors.text },
  roleBadge: { display: 'inline-block', background: colors.surface2, color: colors.textMuted, padding: '1px 7px', borderRadius: '8px', fontSize: '10px', fontWeight: '600', marginTop: '2px' }
};

export default ConversionAnalytics;
